import type { BillablePlant } from '@dru/types';
import { SearchSelect } from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';

/**
 * The plants an invoice can be raised for (docs/37 A6).
 *
 * An invoice bills one plant, so generation names one; there is no "all
 * plants" option. The list comes from `/invoices/plants`, which answers
 * with the plants the caller may bill — for the platform, every plant of
 * the named company.
 *
 * A searchable dropdown: a company can run many plants, and the code and
 * GSTIN are searchable as well as the name.
 */
export function BillablePlantSelect({ tenantId, value, onChange, label, required, error, className = 'w-full' }: {
  tenantId: string; value: string; onChange: (plantId: string) => void;
  label?: string; required?: boolean; error?: string | null; className?: string;
}) {
  const plants = useQuery<{ items: BillablePlant[] }>(
    () => api.get(`/invoices/plants?tenantId=${tenantId}`), [tenantId],
  );
  const items = plants.data?.items ?? [];
  const placeholder = plants.loading
    ? 'Loading plants…'
    : plants.error ? 'Plants could not be loaded — close and try again'
      : items.length === 0 ? 'This company has no plant to bill' : 'Choose the plant to bill…';
  return (
    <SearchSelect
      label={label}
      aria-label={label ? undefined : 'Plant'}
      required={required}
      error={error}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      searchPlaceholder="Search plants by name, code or GSTIN…"
      emptyMessage={placeholder}
      options={items.map((p) => ({
        value: p.id,
        label: `${p.code} · ${p.name}`,
        hint: p.gstin ? `GSTIN ${p.gstin}` : undefined,
      }))}
      className={className}
    />
  );
}
